import { useCallback, useEffect, useState } from 'react'

import { FullscreenLoadingOverlay } from '../../../components/loading-overlay/FullscreenLoadingOverlay'
import { useCurrentTenantConfig } from '../../../utils/tenants'
import { usePermissions } from '../data/hooks'
import { PermissionsContext } from './permissionsContext'

interface PermissionsContextProviderProps {
  children?: React.ReactNode
}

const PermissionsContextProvider: React.FC<PermissionsContextProviderProps> = ({
  children,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Get the current tenant configuration - we'll use this to detect changes
  const currentTenantConfig = useCurrentTenantConfig()
  // Use SWR hooks with revalidateOnFocus: false, dedupingInterval: 0 to prevent caching
  const permissions = usePermissions({
    revalidateOnFocus: false,
    dedupingInterval: 0,
  })

  const refreshPermissions = useCallback(async () => {
    setIsRefreshing(true)

    await permissions.mutate()

    setIsRefreshing(false)
  }, [permissions])

  // Refresh permissions when tenant configuration changes
  useEffect(() => {
    refreshPermissions()
  }, [currentTenantConfig.tenantId, currentTenantConfig.projectId])

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        refreshPermissions,
        isRefreshing,
      }}
    >
      {isRefreshing || permissions.isLoading ? (
        <FullscreenLoadingOverlay />
      ) : (
        children
      )}
    </PermissionsContext.Provider>
  )
}

export default PermissionsContextProvider
